import { alpha, type Components, type Theme } from '@mui/material/styles';

/**
 * Style packs — the T3 tier of the theme engine (theming-dls-engine.md §11A.4).
 *
 * A pack is a named bundle of MUI component overrides selected by
 * `l_portal_theme_base.style_pack_code` and served as `PPThemeStylePack`. Packs are CODE, not
 * config: they contain per-state selectors, pseudo-element rules and focus treatments that the
 * `StyleRules` allowlist deliberately cannot express, precisely because a config edit must never
 * be able to delete a focus ring.
 *
 * WHY OPT-IN RATHER THAN GLOBAL
 * `standard` is empty, so it is exactly today's look. Only a base that names another pack gets
 * one. That means the DeepHealth facilities are untouched by the DLS component styling while the
 * RadNet-family bases can adopt it, and reverting is one column value — not a code rollback.
 *
 * Packs run as a SECOND PASS over an already-created theme, so they can read the resolved
 * palette, typography and transitions. Everything below derives from those, never from
 * hardcoded DLS neutrals: the same pack has to look right for all 20 brands.
 */
export type StylePackName = 'standard' | 'clinical' | 'elevated' | 'radnet-dls';

const PACK_NAMES: readonly StylePackName[] = ['standard', 'clinical', 'elevated', 'radnet-dls'];

export const isStylePackName = (v: unknown): v is StylePackName =>
  typeof v === 'string' && (PACK_NAMES as readonly string[]).includes(v);

/**
 * Border colour for an interactive control boundary, guaranteed to clear 3:1.
 *
 * WHY NOT `rule`. The DLS sets `--rule: var(--carbon-a22)` — 22% black, which composites to
 * ~#C7C7C7 on white and measures **1.69:1**. WCAG 2.2 SC 1.4.11 requires 3:1 for "visual
 * information required to identify user interface components and states", and an input's border
 * is exactly that. 22% is less than half the required contrast.
 *
 * 42% is the minimum alpha that clears 3:1 against white (~#959595, 3.03:1). MUI's own default
 * outlined-input border is rgba(0,0,0,0.23) and fails the same way, so standard-pack fields
 * use this color too.
 *
 * `rule` is still used for section dividers and table cell borders, where the content is
 * decorative and 1.4.11 does not apply. Standard-pack outlined fields use this same color.
 */
export const controlBorderColor = (mode: 'light' | 'dark'): string =>
  mode === 'dark' ? alpha('#ffffff', 0.45) : alpha('#000000', 0.42);

export const uiBorderColor = (theme: Theme): string =>
  controlBorderColor(theme.palette.mode);

/**
 * Geometry transcribed from the RadNet DLS capture via the radnet-dls-mui prototype's
 * `RADNET_BOOTSTRAP_CONTRACT`. Kept as a separate object so drift against the source is a
 * one-file diff rather than an archaeology exercise across 40 selectors.
 */
const DLS = {
  button: { minHeight: 48, smallMinHeight: 44, radius: 999, padY: 14, padX: 28, fontSize: 11 },
  field: { minHeight: 48, radius: 12, padY: 14, padX: 16, fontSize: 15, borderWidth: 1 },
  card: { radius: 16, bodyPadding: 24, borderWidth: 1 },
  tab: { minHeight: 44, radius: 999, padY: 9, padX: 18, fontSize: 13 },
  accordion: { radius: 20, summaryPadY: 22, summaryPadX: 26, bodyPadY: 20, bodyPadX: 26, icon: 36 },
  alert: { radius: 12, leftBorder: 3, fontSize: 14 },
} as const;

/**
 * The RadNet DLS component layer.
 *
 * PHASE C DEVIATIONS — the prototype is faithful to a marketing design system; this is a patient
 * portal with WCAG 2.2 AA obligations and an older-than-average audience. Five deliberate
 * departures, each marked `DEVIATION` inline:
 *
 *   1. Checkbox/radio focus is a 2px outline, not a 12%-alpha box-shadow. In the prototype that
 *      shadow is the ONLY focus indicator on those controls and 12% alpha cannot reach the 3:1
 *      required by SC 1.4.11.
 *   2. Selected tab is a raised paper pill with a real border, not a 14%-on-7% tint. A 7%
 *      luminance delta is not a dependable boundary, and the prototype hides the indicator.
 *   3. Menu items stay sentence case at 14px. The prototype uppercases them at 11px; these are
 *      navigation targets, not eyebrow labels.
 *   4. Chips floor at 12px rather than 10px.
 *   5. Buttons KEEP 11px uppercase — brand-defining, and the 48px min-height carries the touch
 *      target (SC 2.5.8) independently of the label size.
 */
function radnetDlsPack(theme: Theme): Components<Theme> {
  const dark = theme.palette.mode === 'dark';
  const ink = theme.palette.text.primary;
  const surface = theme.palette.background.paper;
  const primary = theme.palette.primary.main;

  // Derived, never hardcoded — the prototype imported ~30 DLS tokens directly, which would have
  // pinned every brand to RadNet's neutrals.
  const rule = theme.palette.rule ?? theme.palette.divider;
  const soft = theme.palette.soft ?? alpha(ink, dark ? 0.08 : 0.04);
  const carbon = theme.palette.carbon ?? (dark ? '#000000' : '#121212');
  const faint = theme.palette.text.secondary;
  const display = theme.typography.h1?.fontFamily ?? theme.typography.fontFamily;
  const body = theme.typography.fontFamily;

  const transition = theme.transitions.create(
    ['background-color', 'border-color', 'box-shadow', 'color', 'transform'],
    { duration: theme.transitions.duration.shorter, easing: theme.transitions.easing.easeOut },
  );

  /** DEVIATION 1 — a real 3:1-capable ring, used everywhere the prototype used a faint shadow. */
  const focusRing = {
    outline: `2px solid ${primary}`,
    outlineOffset: 2,
    boxShadow: 'none',
  } as const;

  return {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: DLS.button.radius,
          fontFamily: display,
          fontSize: DLS.button.fontSize,
          fontWeight: 700,
          letterSpacing: '.18em',
          lineHeight: 1.5,
          minHeight: DLS.button.minHeight,
          padding: `${DLS.button.padY}px ${DLS.button.padX}px`,
          textTransform: 'uppercase',
          transition,
          whiteSpace: 'nowrap',
          gap: theme.spacing(1.25),
          '&:hover': { transform: 'translateY(-1px)' },
          '&:active': { transform: 'translateY(0)' },
          '&.Mui-focusVisible': focusRing,
          '&.Mui-disabled': { opacity: 0.38 },
        },
        sizeSmall: {
          fontSize: 10,
          letterSpacing: '.14em',
          minHeight: DLS.button.smallMinHeight,
          padding: `0 ${theme.spacing(2.5)}`,
        },
        sizeLarge: { fontSize: 12, letterSpacing: '.22em', padding: theme.spacing(2.25, 4.5) },
        outlined: {
          backgroundColor: 'transparent',
          // DEVIATION 6 also applies here: an outlined button's border is what identifies the
          // control, so it needs 3:1 — `rule` at 22% does not reach it.
          borderColor: uiBorderColor(theme),
          borderWidth: '1.5px',
          color: ink,
          '&:hover': { backgroundColor: alpha(ink, 0.06), borderColor: ink, borderWidth: '1.5px' },
          '&:active': { backgroundColor: alpha(ink, 0.12), borderColor: ink },
        },
        text: {
          backgroundColor: soft,
          color: ink,
          '&:hover': { backgroundColor: alpha(ink, dark ? 0.12 : 0.08) },
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: { boxShadow: 'none' },
        grouped: {
          '&:not(:first-of-type)': { borderStartStartRadius: 0, borderEndStartRadius: 0 },
          '&:not(:last-of-type)': { borderStartEndRadius: 0, borderEndEndRadius: 0 },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          backgroundColor: soft,
          borderRadius: '50%',
          color: ink,
          height: 44,
          width: 44,
          padding: 0,
          transition,
          '&:hover': { backgroundColor: alpha(primary, 0.08), color: primary },
          '&:active': { backgroundColor: alpha(primary, 0.18), transform: 'scale(.94)' },
          '&.Mui-focusVisible': focusRing,
        },
        // Still clears the 24x24 minimum (SC 2.5.8).
        sizeSmall: { height: 32, width: 32 },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          backgroundColor: primary,
          color: theme.palette.primary.contrastText,
          height: 48,
          minHeight: 48,
          width: 48,
          '&:hover': {
            backgroundColor: primary,
            boxShadow: `0 12px 28px -10px ${alpha(primary, 0.5)}`,
            transform: 'translateY(-1px)',
          },
          '&.Mui-focusVisible': focusRing,
        },
      },
    },

    // ── Form controls ────────────────────────────────────────────────────────
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          display: 'flex',
          // Explicit: overriding `display` alone left the column direction depending on MUI's own
          // root style still applying. The label-above-field layout is wrong in a row.
          flexDirection: 'column',
          gap: theme.spacing(1),
          '& .MuiFormHelperText-root': { fontSize: 12, margin: 0 },
        },
      },
    },
    MuiInputLabel: {
      // The DLS floats the label above the field rather than notching the border.
      defaultProps: { shrink: true },
      styleOverrides: {
        root: {
          color: ink,
          fontFamily: body,
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1.5,
          margin: 0,
          maxWidth: 'none',
          position: 'static',
          transform: 'none',
          '&.Mui-focused': { color: ink },
          '&.Mui-error': { color: theme.palette.error.main },
        },
        // These SLOT overrides are what actually neutralise MUI's label transform, and the reason
        // they work is the order InputLabel's overridesResolver applies them in:
        //
        //   styles.root, formControl, sizeSmall, shrink, animated, focused, styles[variant]
        //
        // MUI v9 sets the transform through a prop-based `variants` array merged into the
        // component's own emotion class — for outlined + shrink that is
        // `translate(14px, -9px) scale(0.75)`, which parks the label ON the border. The pack hides
        // the notch legend (see MuiOutlinedInput), so there is no gap for it to sit in and the
        // border draws straight THROUGH the label text. It reads as a strikethrough, worst on
        // `error` where a red label lands on a red border.
        //
        // `outlined` is applied LAST, so it wins outright. `shrink` and `sizeSmall` are included
        // because defaultProps forces shrink and the portal uses size="small" throughout.
        sizeSmall: { position: 'static', transform: 'none' },
        shrink: { position: 'static', transform: 'none' },
        outlined: { position: 'static', transform: 'none' },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: { color: ink, fontFamily: body, fontSize: 14, fontWeight: 600 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: surface,
          borderRadius: DLS.field.radius,
          color: ink,
          fontFamily: body,
          fontSize: DLS.field.fontSize,
          // DEVIATION 7 — the DLS sets --fw-light (300) on field text. An input is where a
          // patient verifies what they typed (an MRN, a date of birth); 300 at 15px is thin for
          // that. Body prose stays at the authored 300 via typography_json.
          fontWeight: 400,
          minHeight: DLS.field.minHeight,
          transition,
          '& .MuiOutlinedInput-notchedOutline': {
            // DEVIATION 6 — uiBorderColor, not `rule`. The DLS uses --rule (22% black) here,
            // which measures 1.69:1 and fails SC 1.4.11 for a component boundary.
            borderColor: uiBorderColor(theme),
            borderWidth: DLS.field.borderWidth,
            top: 0,
          },
          // Label is static (above the field), so the notch gap must be closed.
          '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ink },
          // Border weight carries the focus state; the ring reinforces it. The prototype's
          // 0.12 ring alone would not have been dependable.
          '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(primary, 0.24)}` },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primary, borderWidth: 2 },
          '&.Mui-error': { boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.2)}` },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.error.main },
          '&.Mui-disabled': { backgroundColor: alpha(ink, 0.04), opacity: 0.45 },
        },
        input: {
          boxSizing: 'border-box',
          height: DLS.field.minHeight,
          padding: `${DLS.field.padY}px ${DLS.field.padX}px`,
          '&::placeholder': { color: faint, opacity: 1 },
          '&.MuiInputBase-inputMultiline': { height: 'auto', minHeight: 120 },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        // The DLS overrides selects to --r-8 while text inputs stay at --r-12.
        root: { borderRadius: 8 },
        select: {
          alignItems: 'center',
          display: 'flex',
          minHeight: '0 !important',
          paddingInlineEnd: '36px !important',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: { label: { color: ink, fontSize: 14 } },
    },
    MuiCheckbox: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          borderRadius: 4,
          color: rule,
          padding: 4,
          '& .MuiSvgIcon-root': { fontSize: 20 },
          '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: primary },
          // DEVIATION 1 — see the header. The prototype's 12%-alpha shadow was the only
          // indicator here and cannot reach 3:1.
          '&.Mui-focusVisible': focusRing,
        },
      },
    },
    MuiRadio: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          color: rule,
          padding: 4,
          '& .MuiSvgIcon-root': { fontSize: 20 },
          '&.Mui-checked': { color: primary },
          '&.Mui-focusVisible': focusRing,   // DEVIATION 1
        },
      },
    },
    MuiSwitch: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: { height: 24, padding: 0, width: 40 },
        switchBase: {
          padding: 2,
          '&.Mui-checked': {
            color: theme.palette.common.white,
            transform: 'translateX(16px)',
            '& + .MuiSwitch-track': { backgroundColor: primary, opacity: 1 },
          },
          '&.Mui-focusVisible': focusRing,   // DEVIATION 1 — absent entirely in the prototype
        },
        thumb: { height: 20, width: 20 },
        track: { backgroundColor: alpha(ink, 0.25), borderRadius: 999, opacity: 1 },
      },
    },

    // ── Surfaces ─────────────────────────────────────────────────────────────
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: surface,
          backgroundImage: 'none',
          border: `${DLS.card.borderWidth}px solid ${rule}`,
          borderRadius: DLS.card.radius,
          boxShadow: 'none',
          color: ink,
          overflow: 'hidden',
          textAlign: 'start',
          transition,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: DLS.card.bodyPadding,
          '&:last-child': { paddingBottom: DLS.card.bodyPadding },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: 'transparent' },
          '&.Mui-focusVisible': { outline: `2px solid ${primary}`, outlineOffset: -3 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          border: `1px solid ${rule}`,
          borderRadius: 999,
          color: ink,
          fontFamily: display,
          fontSize: 12,          // DEVIATION 4 — prototype used 10px
          fontWeight: 700,
          height: 36,
          letterSpacing: '.12em',
          textTransform: 'uppercase',
        },
        sizeSmall: {
          backgroundColor: soft,
          border: 0,
          fontFamily: body,
          fontSize: 12,          // DEVIATION 4 — prototype used 11px
          fontWeight: 500,
          height: 26,
          letterSpacing: '.02em',
          textTransform: 'none',
        },
        colorPrimary: {
          backgroundColor: alpha(primary, 0.08),
          borderColor: primary,
          color: primary,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          borderRadius: 999,
          fontFamily: body,
          fontSize: 11,
          fontWeight: 700,
          height: 20,
          minWidth: 20,
          padding: `0 ${theme.spacing(0.75)}`,
        },
      },
    },

    // ── Wayfinding ───────────────────────────────────────────────────────────
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(ink, 0.07),
          borderRadius: 999,
          minHeight: 52,
          padding: 4,
          width: 'fit-content',
        },
        // A pill radius is a HORIZONTAL idiom. On orientation="vertical" the container is taller
        // than it is wide, so borderRadius 999 clamps to 50% of each axis and renders a full
        // ELLIPSE — and because MUI's Tabs-root sets `overflow: hidden`, the tab labels are
        // genuinely clipped at the corners, not merely overflowing. The Account page's vertical
        // section nav is where this showed up.
        //
        // `vertical` is a real theme slot: Tabs' overridesResolver is
        //   [ ...,  styles.root, ownerState.vertical && styles.vertical ]
        // so this is applied AFTER root and wins without any specificity trickery.
        //
        // shape.borderRadius (the base's section radius) keeps it consistent with other containers
        // — 12px on radnet-dls, 4px on dh — instead of inventing a third value.
        vertical: {
          borderRadius: theme.shape.borderRadius,
          // root sets `fit-content` for a horizontal pill group; a vertical nav should fill its
          // column so the tinted track lines up with the labels.
          width: '100%',
        },
        // DEVIATION 2 — the prototype hides the indicator and relies on a 7% tint delta.
        // Selection is carried by the raised paper pill below instead, which has a real border.
        indicator: { display: 'none' },
        scrollButtons: { borderRadius: '50%' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          borderRadius: DLS.tab.radius,
          color: faint,
          fontFamily: body,
          fontSize: DLS.tab.fontSize,
          fontWeight: 600,
          letterSpacing: 0,
          minHeight: DLS.tab.minHeight,
          minWidth: 0,
          padding: `${DLS.tab.padY}px ${DLS.tab.padX}px`,
          textTransform: 'none',
          transition,
          '&:hover': { backgroundColor: alpha(ink, 0.07), color: ink },
          '&.Mui-focusVisible': focusRing,
          border: '1px solid transparent',
          '&.Mui-selected': {
            // DEVIATION 2, revised 2026-07-28 against the DLS reference screenshots.
            //
            // The first attempt used a white paper pill. That reads clearly but does NOT match
            // the DLS, where the selected pill is DARKER than the track. Matching the reference
            // and keeping the safety instead: the DLS tint plus a 1px border, so the boundary
            // no longer depends on a ~7% luminance delta alone and survives greyscale and
            // forced-colours mode.
            backgroundColor: alpha(ink, 0.16),
            border: `1px solid ${alpha(ink, 0.22)}`,
            boxShadow: `0 1px 2px ${alpha(ink, 0.12)}`,
            color: ink,
            fontWeight: 700,
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: { backgroundColor: alpha(ink, 0.08), borderRadius: 12, gap: 2, padding: 4 },
        grouped: { border: '0 !important', borderRadius: '9px !important' },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: ink,
          fontFamily: body,
          fontSize: 13,
          fontWeight: 500,
          minHeight: 44,
          padding: theme.spacing(1.375, 2.25),
          textTransform: 'none',
          '&.Mui-focusVisible': focusRing,
          '&.Mui-selected': {
            backgroundColor: surface,
            color: primary,
            fontWeight: 700,
            boxShadow: `0 1px 3px ${alpha(ink, 0.16)}`,
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: { color: faint, fontSize: 14 },
        separator: { marginInline: theme.spacing(1) },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          backgroundColor: surface,
          border: `1px solid ${rule}`,
          borderRadius: 8,
          color: ink,
          minHeight: 44,
          minWidth: 44,
          '&:hover': { backgroundColor: soft, color: primary },
          '&.Mui-focusVisible': focusRing,
          '&.Mui-selected': {
            backgroundColor: primary,
            borderColor: primary,
            color: theme.palette.primary.contrastText,
            '&:hover': { backgroundColor: primary },
          },
        },
      },
    },

    // ── Disclosure ───────────────────────────────────────────────────────────
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: surface,
          backgroundImage: 'none',
          border: `1px solid ${rule}`,
          boxShadow: 'none',
          color: ink,
          overflow: 'hidden',
          '&:before': { display: 'none' },
          '&:first-of-type': {
            borderStartStartRadius: DLS.accordion.radius,
            borderStartEndRadius: DLS.accordion.radius,
          },
          '&:last-of-type': {
            borderEndStartRadius: DLS.accordion.radius,
            borderEndEndRadius: DLS.accordion.radius,
          },
          '& + &': { borderTop: 0 },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 0,
          padding: `${DLS.accordion.summaryPadY}px ${DLS.accordion.summaryPadX}px`,
          '&.Mui-expanded': { minHeight: 0 },
          '&.Mui-focusVisible': { ...focusRing, backgroundColor: 'transparent' },
        },
        content: {
          fontFamily: display,
          fontSize: 16,
          fontWeight: 700,
          letterSpacing: '.06em',
          margin: 0,
          '&.Mui-expanded': { color: primary, margin: 0 },
        },
        expandIconWrapper: {
          alignItems: 'center',
          backgroundColor: primary,
          borderRadius: '50%',
          color: theme.palette.primary.contrastText,
          display: 'flex',
          height: DLS.accordion.icon,
          justifyContent: 'center',
          width: DLS.accordion.icon,
          '&.Mui-expanded': { transform: 'rotate(45deg) scale(1.08)' },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${rule}`,
          padding: `${DLS.accordion.bodyPadY}px ${DLS.accordion.bodyPadX}px`,
        },
      },
    },

    // ── Feedback ─────────────────────────────────────────────────────────────
    MuiAlert: {
      styleOverrides: {
        root: {
          alignItems: 'center',
          border: '1px solid transparent',
          borderInlineStart: `${DLS.alert.leftBorder}px solid currentColor`,
          borderRadius: DLS.alert.radius,
          fontSize: DLS.alert.fontSize,
          padding: theme.spacing(1.5, 2),
        },
      },
      // MUI v9 removed the per-severity slot keys (`standardSuccess` et al.) from
      // styleOverrides; `variants` with a props matcher is the replacement.
      //
      // Derived per severity so every brand gets a correct wash, rather than the DLS's
      // hardcoded *-soft neutrals. `.dark` for the text keeps the label above 4.5:1 on a
      // 8–10% tint of the same hue, which `.main` would not reliably manage.
      variants: (['success', 'warning', 'error', 'info'] as const).map((severity) => ({
        props: { severity, variant: 'standard' as const },
        style: {
          backgroundColor: alpha(theme.palette[severity].main, severity === 'error' ? 0.08 : 0.1),
          color: theme.palette[severity].dark,
        },
      })),
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: surface,
          borderRadius: 12,
          boxShadow: `0 4px 8px 3px ${alpha('#000', 0.08)}, 0 1px 3px ${alpha('#000', 0.08)}`,
          color: ink,
          fontSize: 14,
          gap: 14,
          padding: theme.spacing(2, 2.5),
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { backgroundColor: soft, borderRadius: 999, height: 8 },
        bar: { backgroundColor: primary, borderRadius: 999 },
      },
    },
    MuiCircularProgress: {
      styleOverrides: { root: { color: primary } },
    },

    // ── Overlays ─────────────────────────────────────────────────────────────
    MuiMenu: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        paper: {
          border: `1px solid ${rule}`,
          borderRadius: 16,
          boxShadow: `0 20px 40px -10px ${alpha('#000', 0.32)}, 0 4px 12px -2px ${alpha('#000', 0.12)}`,
          minWidth: 180,
          padding: 8,
        },
        list: { padding: 0 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          // DEVIATION 3 — sentence case at 14px. The prototype used 11px uppercase; menu items
          // are navigation targets, not eyebrow labels.
          color: ink,
          fontFamily: body,
          fontSize: 14,
          fontWeight: 500,
          letterSpacing: 0,
          minHeight: 44,
          padding: theme.spacing(1.25, 1.75),
          textTransform: 'none',
          '&:hover, &.Mui-focusVisible': { backgroundColor: alpha(primary, 0.06), color: primary },
          '&.Mui-selected': {
            backgroundColor: alpha(primary, 0.1),
            color: primary,
            fontWeight: 700,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          border: `1px solid ${rule}`,
          borderRadius: 16,
          boxShadow: `0 6px 10px 4px ${alpha('#000', 0.08)}, 0 2px 3px ${alpha('#000', 0.08)}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${rule}`,
          fontFamily: display,
          fontSize: 24,
          fontWeight: 700,
          padding: 24,
        },
      },
    },
    MuiDialogContent: { styleOverrides: { root: { padding: '24px !important' } } },
    MuiDialogActions: {
      styleOverrides: { root: { borderTop: `1px solid ${rule}`, gap: 8, padding: 24 } },
    },
    MuiDrawer: {
      // Only backgroundImage. The prototype also set `padding: 26` and `gap: 16`, which are
      // right for the DLS's slide-out marketing nav and WRONG for an app shell: our sidebar is
      // a PERMANENT drawer whose collapsed rail is 64px wide, so 26px of paper padding on each
      // side would leave 12px of content and destroy the rail. AppLayout owns the sidebar's own
      // padding and spacing; the pack must not fight it.
      //
      // The edge border deliberately stays with the base theme so it follows the `Divider` token.
      styleOverrides: { paper: { backgroundImage: 'none' } },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          backgroundColor: carbon,
          borderRadius: 8,
          color: '#F5F5F7',
          fontFamily: body,
          fontSize: 14,
          lineHeight: 1.5,
          maxWidth: 240,
          padding: theme.spacing(1, 2),
        },
        arrow: { color: carbon },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          border: `1px solid ${rule}`,
          borderRadius: 14,
          boxShadow: theme.shadows[2],
          fontSize: 14,
        },
      },
    },

    // ── Data display ─────────────────────────────────────────────────────────
    MuiTable: { styleOverrides: { root: { color: ink } } },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${rule}`, color: ink, fontSize: 14, padding: '12px 16px' },
        head: {
          backgroundColor: soft,
          fontFamily: display,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '.06em',
          textTransform: 'uppercase',
        },
      },
    },
    MuiTableRow: { styleOverrides: { root: { '&:hover': { backgroundColor: soft } } } },
    MuiList: {
      styleOverrides: {
        root: { border: `1px solid ${rule}`, borderRadius: 12, overflow: 'hidden', padding: 0 },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${rule}`,
          gap: 12,
          minHeight: 48,
          padding: '12px 16px',
          '&:last-child': { borderBottom: 0 },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${rule}`,
          fontSize: 14,
          minHeight: 48,
          padding: '12px 16px',
          '&:last-child': { borderBottom: 0 },
          '&:hover': { backgroundColor: soft },
          '&.Mui-focusVisible': focusRing,
          '&.Mui-selected': {
            backgroundColor: alpha(primary, 0.1),
            // Inline-start so the marker flips correctly in Arabic RTL.
            boxShadow: `inset 3px 0 0 ${primary}`,
            color: primary,
          },
        },
      },
    },
  };
}

/**
 * `clinical` — tightens the DLS geometry for dense screens without changing its identity.
 * Squarer corners, shorter controls, sentence-case buttons.
 */
function clinicalPack(theme: Theme): Components<Theme> {
  const base = radnetDlsPack(theme);
  return {
    ...base,
    MuiButton: {
      ...base.MuiButton,
      styleOverrides: {
        ...(base.MuiButton?.styleOverrides as object),
        root: {
          ...((base.MuiButton?.styleOverrides as { root?: object } | undefined)?.root ?? {}),
          borderRadius: 4,
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 0,
          minHeight: 36,
          padding: theme.spacing(1, 2),
          textTransform: 'none',
        },
      },
    },
    MuiTabs: {
      ...base.MuiTabs,
      styleOverrides: {
        root: { backgroundColor: 'transparent', borderRadius: 0, minHeight: 44, padding: 0 },
        // Dense screens get the conventional underline back — it costs no vertical space.
        indicator: { height: 2 },
      },
    },
  };
}

/** `elevated` — the DLS layer with shadow-based lift instead of hairline borders. */
function elevatedPack(theme: Theme): Components<Theme> {
  const base = radnetDlsPack(theme);
  const lift = `0 8px 24px -10px ${alpha('#000', 0.22)}, 0 2px 6px -2px ${alpha('#000', 0.1)}`;
  return {
    ...base,
    MuiCard: {
      styleOverrides: {
        root: {
          ...((base.MuiCard?.styleOverrides as { root?: object } | undefined)?.root ?? {}),
          border: 0,
          boxShadow: lift,
        },
      },
    },
  };
}

/**
 * The registry. `standard` is intentionally empty — a base that does not name a pack keeps the
 * portal's existing component styling exactly, which is what makes this safe to ship.
 */
export const STYLE_PACKS: Record<StylePackName, (theme: Theme) => Components<Theme>> = {
  standard: () => ({}),
  clinical: clinicalPack,
  elevated: elevatedPack,
  'radnet-dls': radnetDlsPack,
};
